import { useCallback, useEffect, useMemo, useState } from 'react';

import type { LoadState } from '../../components/workflowShared';
import type { WorkspaceScanSummary } from '../../components/screens/RunScreens';
import {
  runGetDevWorkspaceScans,
  runIsDevGitHubAccountEnabled,
  runRequestJson,
  type RunIssueAcceptanceStatus
} from './appRunData';
import { runIssueEnhanceIssues, type RunIssueViewModelEnhanced } from '../../run/RunIssueDashboard';
import { isDevRunId, loadScanReport } from './appScanApi';

/*
 * The workspace issue register. The backend stores findings, their review status and the scanned
 * folders per scan, so the register is assembled here: one row per finding across all finished
 * scans, matched by a fingerprint of title and primary file.
 */

export type RegisterStatus = 'open' | 'acknowledged' | 'fixed' | 'dismissed';

interface RegisterOccurrence {
  scanId: string;
  issueId: string;
}

export interface RegisterIssue extends RunIssueViewModelEnhanced {
  /** Fingerprint shared by the same finding in different scans; also used as `id`. */
  key: string;
  status: RegisterStatus;
  /** Newest scan first. */
  occurrences: RegisterOccurrence[];
  firstSeen: WorkspaceScanSummary;
  lastSeen: WorkspaceScanSummary;
}

export interface IssueRegister {
  state: LoadState;
  workspaceId?: string;
  /** Every scan of the workspace, newest first. */
  scans: WorkspaceScanSummary[];
  /** Finished scans whose findings are in the register, newest first. */
  reportScans: WorkspaceScanSummary[];
  issues: RegisterIssue[];
}

interface ScanReport {
  issues: RunIssueViewModelEnhanced[];
  /** Folders the scan covered; empty means the whole repository. */
  scopePaths: string[];
}

export interface RegisterData {
  state: LoadState;
  workspaceId?: string;
  scans: WorkspaceScanSummary[];
  reports: Record<string, ScanReport>;
}

const REPORTED_SCAN_STATUSES = ['completed', 'partial'];
// Each report is a separate request; older scans rarely add findings the newer ones lack.
const MAX_REPORT_SCANS = 10;
const SEVERITY_ORDER = ['critical', 'high', 'medium', 'low', 'informational', 'gas', 'other'];

const EMPTY_DATA: RegisterData = { state: 'idle', scans: [], reports: {} };

const normalize = (value: string): string => value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

const getIssueKey = (issue: RunIssueViewModelEnhanced): string => {
  return `${normalize(issue.cleanTitle)}|${(issue.files[0] ?? '').toLowerCase()}`;
};

const getScanTime = (scan: WorkspaceScanSummary): number => new Date(scan.createdAt).getTime();

const normalizePath = (path: string): string => path.trim().replace(/\\/g, '/').replace(/^\.?\/+/, '').replace(/\/+$/, '');

/** Whether a scan limited to `scopePaths` looked at the finding's primary file. */
const coversFinding = (scopePaths: string[], files: string[]): boolean => {
  if (scopePaths.length === 0) {
    return true;
  }

  const file = files[0] ? normalizePath(files[0]) : '';

  return Boolean(file) && scopePaths.some((scopePath) => {
    const folder = normalizePath(scopePath);

    return file === folder || file.startsWith(`${folder}/`);
  });
};

/**
 * Dismissed wins, since it is a deliberate decision. Otherwise a finding counts as fixed when the
 * newest completed scan of the same branch that covered its file ran after it was last seen and no
 * longer reports it. Scans scoped to other folders say nothing about the finding.
 */
const deriveStatus = (
  issue: RegisterIssue,
  reportScans: WorkspaceScanSummary[],
  reports: Record<string, ScanReport>
): RegisterStatus => {
  if (issue.acceptanceStatus === 'accepted') {
    return 'dismissed';
  }

  const newestCoveringScan = reportScans.find((scan) => {
    return scan.status === 'completed'
      && scan.branch === issue.lastSeen.branch
      && coversFinding(reports[scan.id].scopePaths, issue.files);
  });

  if (
    newestCoveringScan
    && !issue.occurrences.some((occurrence) => occurrence.scanId === newestCoveringScan.id)
    && getScanTime(newestCoveringScan) > getScanTime(issue.lastSeen)
  ) {
    return 'fixed';
  }

  return issue.acceptanceStatus === 'acknowledged' ? 'acknowledged' : 'open';
};

export const buildRegister = (data: RegisterData): IssueRegister => {
  const reportScans = data.scans.filter((scan) => data.reports[scan.id]);
  const byKey = new Map<string, RegisterIssue>();

  // Newest scan first, so each row carries the newest wording and review status.
  for (const scan of reportScans) {
    for (const issue of data.reports[scan.id].issues) {
      const key = getIssueKey(issue);
      const existing = byKey.get(key);
      const occurrence = { scanId: scan.id, issueId: issue.id };

      if (existing) {
        existing.occurrences.push(occurrence);
        existing.firstSeen = scan;
      } else {
        byKey.set(key, {
          ...issue,
          id: key,
          key,
          status: 'open',
          occurrences: [occurrence],
          firstSeen: scan,
          lastSeen: scan
        });
      }
    }
  }

  const issues = Array.from(byKey.values())
    .map((issue) => ({ ...issue, status: deriveStatus(issue, reportScans, data.reports) }))
    .sort((left, right) => {
      return SEVERITY_ORDER.indexOf(left.severity) - SEVERITY_ORDER.indexOf(right.severity)
        || getScanTime(right.firstSeen) - getScanTime(left.firstSeen);
    });

  return { state: data.state, workspaceId: data.workspaceId, scans: data.scans, reportScans, issues };
};

/** Fetches a workspace's scans and the findings of its newest finished ones. */
export const loadRegisterData = async (workspaceId: string, maxReportScans = MAX_REPORT_SCANS): Promise<RegisterData> => {
  const scans = runIsDevGitHubAccountEnabled()
    ? runGetDevWorkspaceScans(workspaceId)
    : (await runRequestJson<{ scans?: WorkspaceScanSummary[] }>(
      `/workspaces/${encodeURIComponent(workspaceId)}/scans`
    )).scans ?? [];
  const finished = scans
    .filter((scan) => REPORTED_SCAN_STATUSES.includes(scan.status))
    .slice(0, maxReportScans);
  // One unreadable report should not hide the findings of the others.
  const results = await Promise.allSettled(finished.map((scan) => loadScanReport(scan.id)));
  const reports: Record<string, ScanReport> = {};

  results.forEach((result, index) => {
    if (result.status === 'fulfilled') {
      reports[finished[index].id] = {
        issues: runIssueEnhanceIssues(result.value.issues),
        scopePaths: result.value.scopePaths
      };
    } else {
      console.error(`Failed to load findings of scan ${finished[index].id}:`, result.reason);
    }
  });

  return { state: 'ready', workspaceId, scans, reports };
};

/** Loads the issue register of a workspace. Bump `reloadKey` to refetch, for example after a scan finishes. */
export function useWorkspaceIssues(workspaceId: string, reloadKey = 0) {
  const [data, setData] = useState<RegisterData>(EMPTY_DATA);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    if (!workspaceId) {
      setData(EMPTY_DATA);
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      // Keep this workspace's register visible while a reload is in flight.
      setData((current) => current.state === 'ready' && current.workspaceId === workspaceId
        ? current
        : { ...EMPTY_DATA, state: 'loading', workspaceId });

      try {
        const loaded = await loadRegisterData(workspaceId);

        if (!cancelled) {
          setData(loaded);
        }
      } catch (error) {
        console.error('Failed to load workspace issues:', error);

        if (!cancelled) {
          setData({ ...EMPTY_DATA, state: 'error', workspaceId });
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [workspaceId, reloadKey, retryKey]);

  const register = useMemo(() => buildRegister(data), [data]);

  /** Saves a review status on every scan that reported the findings; `null` reopens them. */
  const setStatus = useCallback(async (keys: string[], status: RunIssueAcceptanceStatus | null) => {
    const targets = register.issues
      .filter((issue) => keys.includes(issue.key))
      .flatMap((issue) => issue.occurrences);

    try {
      await Promise.all(targets.map((target) => {
        return isDevRunId(target.scanId)
          ? Promise.resolve()
          : runRequestJson(
            `/runs/${encodeURIComponent(target.scanId)}/issues/${encodeURIComponent(target.issueId)}/acceptance`,
            { method: 'POST', body: JSON.stringify({ status: status ?? 'open' }) }
          );
      }));
    } catch (error) {
      // Some scans may have saved already; reload so the register shows what the backend has.
      setRetryKey((key) => key + 1);
      throw error;
    }

    setData((current) => ({
      ...current,
      reports: Object.fromEntries(Object.entries(current.reports).map(([scanId, report]) => [
        scanId,
        {
          ...report,
          issues: report.issues.map((issue) => {
            return targets.some((target) => target.scanId === scanId && target.issueId === issue.id)
              ? { ...issue, acceptanceStatus: status ?? undefined }
              : issue;
          })
        }
      ]))
    }));
  }, [register.issues]);

  const reload = useCallback(() => setRetryKey((key) => key + 1), []);

  return { register, setStatus, reload };
}
